import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { Add, RemoveCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { without } from "lodash";
import EnumSelect from "../../common/EnumSelect";
import AddButton from "../../common/AddButton";
import {
  TrailerCompartmentInput,
  TrailerCompartmentUnit,
} from "../../../graphql/generated";
import { hasNumberValue } from "../../../utils/form/hasValue";
import { testIds } from "./CompartmentsForm.testIds";

type CompartmentsFormProps = {
  compartments: TrailerCompartmentInput[];
  onChange: (compartments: TrailerCompartmentInput[]) => void;
  getFieldError: (
    field: string,
    partialPathMatch?: boolean
  ) => string | undefined;
};

const numberOrNull = (value: string) =>
  hasNumberValue(value) ? Number(value) : null;

const CompartmentsForm = ({
  compartments,
  onChange,
  getFieldError,
}: CompartmentsFormProps) => {
  const { t } = useTranslation(["assets", "common"]);

  const update = (
    target: TrailerCompartmentInput,
    patch: Partial<TrailerCompartmentInput>
  ) => onChange(compartments.map((c) => (c === target ? { ...c, ...patch } : c)));

  return (
    <>
      {compartments.map((compartment, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={2}
          sx={{ mb: 2, mt: 1, alignItems: "center" }}
        >
          <TextField
            label={t("assets:trailer.identifier", "Identifier")}
            fullWidth
            value={compartment.identifier || ""}
            error={!!getFieldError(`compartments.${index}.identifier`)}
            helperText={getFieldError(`compartments.${index}.identifier`)}
            onChange={(e) => update(compartment, { identifier: e.target.value })}
            inputProps={
              {
                "data-testid": `${testIds.compartmentsFormIdentifier}-${compartment.identifier}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
          <TextField
            label={t(
              "assets:trailer.compartmentCapacity",
              "Compartment Capacity"
            )}
            fullWidth
            type="number"
            value={String(compartment.capacity) || ""}
            error={!!getFieldError(`compartments.${index}.capacity`)}
            helperText={getFieldError(`compartments.${index}.capacity`)}
            onChange={(e) =>
              update(compartment, { capacity: parseFloat(e.target.value) })
            }
            inputProps={
              {
                "data-testid": `${testIds.compartmentsFormCapacity}-${compartment.identifier}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
          <EnumSelect data-testid={`compartments-form-unit-${compartment.identifier}`}
            enumObject={TrailerCompartmentUnit}
            label={t("assets:commodity.unit", "Unit")}
            fullWidth
            value={compartment.unit}
            onChange={(_e, value) =>
              value && update(compartment, { unit: value })
            }
          />
          <TextField
            label={t("assets:trailer.minVolume", "Min Volume")}
            fullWidth
            type="number"
            value={compartment.minVolume ?? ""}
            error={!!getFieldError(`compartments.${index}.minVolume`)}
            helperText={getFieldError(`compartments.${index}.minVolume`)}
            onChange={(e) =>
              update(compartment, { minVolume: numberOrNull(e.target.value) })
            }
            inputProps={
              {
                "data-testid": `${testIds.compartmentsFormMinVolume}-${compartment.identifier}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
          <TextField
            label={t("assets:trailer.maxVolume", "Max Volume")}
            fullWidth
            type="number"
            value={compartment.maxVolume ?? ""}
            error={!!getFieldError(`compartments.${index}.maxVolume`)}
            helperText={getFieldError(`compartments.${index}.maxVolume`)}
            onChange={(e) =>
              update(compartment, { maxVolume: numberOrNull(e.target.value) })
            }
            inputProps={
              {
                "data-testid": `${testIds.compartmentsFormMaxVolume}-${compartment.identifier}`,
              } as React.InputHTMLAttributes<HTMLInputElement>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={compartment.heavyOnly || false}
                onChange={(e) =>
                  update(compartment, { heavyOnly: e.target.checked })
                }
                inputProps={
                  {
                    "data-testid": `${testIds.compartmentsFormHeavyOnly}-${compartment.identifier}`,
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            label={t("assets:trailer.heavyOnly", "Heavy Only")}
          />
          {index < compartments.length - 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={compartment.hasDoubleBulkheadAfter || false}
                  onChange={(e) =>
                    update(compartment, {
                      hasDoubleBulkheadAfter: e.target.checked,
                    })
                  }
                  inputProps={
                    {
                      "data-testid": `${testIds.compartmentsFormDoubleBulkhead}-${compartment.identifier}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                />
              }
              label={t(
                "assets:trailer.doubleBulkheadBetween",
                "Double Bulkhead ({{a}} and {{b}})",
                {
                  a: index + 1,
                  b: index + 2,
                }
              )}
            />
          )}
          <IconButton
            data-testid={`${testIds.compartmentsFormRemove}-${compartment.identifier}`}
            aria-label="remove compartment"
            onClick={() => onChange(without(compartments, compartment))}
          >
            <RemoveCircle />
          </IconButton>
        </Stack>
      ))}
      <Stack
        direction="row"
        spacing={2}
        sx={{ m: 1, display: "flex", alignItems: "center" }}
      >
        <AddButton
          sx={{ height: 50, width: 20 }}
          id="add-compartment-button"
          data-testid="compartments-form-add-compartment"
          aria-label={t("assets:trailer.addCompartment", "Add Compartment")}
          onClick={() =>
            onChange(
              compartments.concat({
                identifier: `Compartment #${compartments.length + 1}`,
                capacity: 0,
                unit: TrailerCompartmentUnit.Gallons,
                minVolume: null,
                maxVolume: null,
                heavyOnly: false,
                hasDoubleBulkheadAfter: false,
              })
            )
          }
        >
          <Add />
        </AddButton>
        <span>{t("assets:trailer.addCompartment", "Add Compartment")}</span>
      </Stack>
    </>
  );
};

export default CompartmentsForm;
